import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCheckCircle } from '@/icons';
import { authApiService } from '../services/authApiService';
import { debugError } from '../utils/debug';
import { CustomToggle } from '../components/CustomToggle';
import { TermsAndConditionsContent } from '../components/TermsAndConditionsContent';

type SignupStep = 'setup' | 'complete';

interface UserInfo {
    userId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    hasPasswordSet?: boolean;
}

/**
 * Signup page component
 * Single-step process: Set password and accept terms and conditions
 */
export const SignupPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);

    const token = (searchParams.get('token') || '').trim();

    const [currentStep, setCurrentStep] = useState<SignupStep>('setup');
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [isValidating, setIsValidating] = useState(true);
    const [isValidToken, setIsValidToken] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Password form state
    const [passwordData, setPasswordData] = useState({
        newPassword: '',
        confirmPassword: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState<{
        newPassword?: string;
        confirmPassword?: string;
    }>({});

    // T&C state
    const [isSubmittingTerms, setIsSubmittingTerms] = useState(false);
    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

    // Validate token on mount
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setIsValidating(false);
                setIsValidToken(false);
                setError('Invalid signup link. Please contact your administrator.');
                return;
            }

            try {
                const response = await authApiService.validateSignupToken(token);
                if (response.data.success) {
                    setIsValidToken(true);
                    setUserInfo({
                        userId: response.data.userId,
                        email: response.data.email,
                        firstName: response.data.firstName,
                        lastName: response.data.lastName,
                        hasPasswordSet: response.data.hasPasswordSet,
                    });
                    // Always show the setup step (password + T&C)
                    setCurrentStep('setup');
                } else {
                    // Token may be a password reset token (user landed on signup with reset link)
                    try {
                        const resetResponse = await authApiService.validateResetToken(token);
                        if (resetResponse.data.valid) {
                            navigate(`/auth/reset-password?token=${encodeURIComponent(token)}`, { replace: true });
                            return;
                        }
                    } catch {
                        // Not a valid reset token; show signup error below
                    }
                    setError(response.data.message || 'Invalid or expired signup token. Please contact your administrator.');
                }
            } catch (err) {
                debugError('Error validating signup token:', err);
                setError('An error occurred while validating the signup link. Please try again.');
            } finally {
                setIsValidating(false);
            }
        };

        validateToken();
    }, [token]);

    // Set page title
    useEffect(() => {
        document.title = 'Complete Your Account Setup | Calculist';
    }, []);

    const validatePasswordForm = (): { isValid: boolean; errors: typeof passwordErrors } => {
        const errors: typeof passwordErrors = {};

        if (!passwordData.newPassword) {
            errors.newPassword = 'Password is required';
        } else if (passwordData.newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters long';
        }

        if (!passwordData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (passwordData.newPassword && passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setPasswordErrors(errors);
        return { isValid: Object.keys(errors).length === 0, errors };
    };

    // Check if button should be enabled - only requires toggle to be checked
    const isButtonEnabled = (): boolean => {
        return hasAcceptedTerms && !isSubmittingPassword && !isSubmittingTerms;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate password if it hasn't been set yet
        if (!userInfo?.hasPasswordSet) {
            const validation = validatePasswordForm();
            if (!validation.isValid) {
                // Validation errors are already set in passwordErrors state
                // They will display inline below the form fields
                // Don't set general error state - that would trigger the "Invalid Signup Link" screen
                return; // Don't proceed if validation fails
            }
        }

        // Check T&C acceptance
        if (!hasAcceptedTerms) {
            setError('You must accept the Terms and Conditions to continue.');
            return; // Don't proceed if T&C not accepted
        }

        // Clear errors only when validation passes
        setError(null);
        setPasswordErrors({});

        // Only set submitting flags after validation passes
        setIsSubmittingPassword(true);
        setIsSubmittingTerms(true);
        setError(null);

        try {
            // If password is already set, just accept T&C
            if (userInfo?.hasPasswordSet) {
                const termsResponse = await authApiService.acceptTermsAndConditions(token);
                if (termsResponse.data.success) {
                    setCurrentStep('complete');
                } else {
                    setError(termsResponse.data.message || 'Failed to accept terms and conditions. Please try again.');
                }
            } else {
                // Complete signup: set password and accept T&C in one atomic operation
                const response = await authApiService.completeSignup(
                    token,
                    passwordData.newPassword,
                    passwordData.confirmPassword
                );

                if (response.data.success) {
                    setCurrentStep('complete');
                } else {
                    setError(response.data.message || 'Failed to complete account setup. Please try again.');
                }
            }
        } catch (err: any) {
            debugError('Failed to complete signup:', err);
            setError(err?.message || 'An error occurred during signup. Please try again.');
        } finally {
            setIsSubmittingPassword(false);
            setIsSubmittingTerms(false);
        }
    };

    const togglePasswordVisibility = (field: 'password' | 'confirm') => {
        if (field === 'password') {
            setShowPassword(!showPassword);
        } else {
            setShowConfirmPassword(!showConfirmPassword);
        }
    };

    if (isValidating) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background,
                padding: theme.spacing.md,
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: theme.spacing.md,
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: `4px solid ${theme.colors.border.default}`,
                        borderTop: `4px solid ${theme.colors.primary}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                    }}></div>
                    <p style={themedStyles.text.body}>Validating signup link...</p>
                </div>
            </div>
        );
    }

    if (!isValidToken || error) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background,
                padding: theme.spacing.md,
            }}>
                <div style={themedStyles.card.default}>
                    <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                        <img
                            src="/logo-name.svg"
                            alt="Calculist"
                            style={{
                                height: '64px',
                                width: 'auto',
                                marginBottom: theme.spacing.md,
                            }}
                        />
                    </div>

                    {error && (
                        <div style={themedStyles.alert.error}>
                            <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
                            <div>
                                <strong>Invalid Signup Link</strong>
                                <p style={{ margin: '4px 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.status.error }}>
                                    {error}
                                </p>
                            </div>
                        </div>
                    )}

                    <div style={{ marginTop: theme.spacing.lg, textAlign: 'center' }}>
                        <button
                            type="button"
                            onClick={() => navigate('/auth/login')}
                            style={themedStyles.button.primary}
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (currentStep === 'complete') {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background,
                padding: theme.spacing.md,
            }}>
                <div style={themedStyles.card.default}>
                    <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                        <img
                            src="/logo-name.svg"
                            alt="Calculist"
                            style={{
                                height: '64px',
                                width: 'auto',
                                marginBottom: theme.spacing.md,
                            }}
                        />
                    </div>

                    <div style={themedStyles.alert.success}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '20px', flexShrink: 0 }} />
                        <div>
                            <strong>Account Setup Complete!</strong>
                            <p style={{ margin: '4px 0 0 0', fontSize: theme.typography.fontSize.sm }}>
                                Your account has been activated successfully. You can now log in with your email and password.
                            </p>
                        </div>
                    </div>

                    <div style={{ marginTop: theme.spacing.lg, textAlign: 'center' }}>
                        <button
                            type="button"
                            onClick={() => navigate('/auth/login')}
                            style={themedStyles.button.primary}
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Setup step: Password (if needed) + Terms and Conditions
    if (currentStep === 'setup') {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.colors.background,
            }}>
                <div style={{
                    maxWidth: '800px',
                    width: '100%',
                    margin: '0 auto',
                    padding: theme.spacing.lg,
                }}>
                    <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                        <img
                            src="/logo-name.svg"
                            alt="Calculist"
                            style={{
                                height: '64px',
                                width: 'auto',
                                marginBottom: theme.spacing.md,
                            }}
                        />
                        <h1 style={{
                            ...themedStyles.text.heading,
                            fontSize: theme.typography.fontSize.xl,
                            marginBottom: theme.spacing.sm,
                        }}>
                            Welcome, {userInfo?.firstName || 'there'}!
                        </h1>
                        <p style={themedStyles.text.muted}>
                            Complete your account setup by creating a password and accepting the terms and conditions.
                        </p>
                    </div>

                    {error && (
                        <div style={{
                            ...themedStyles.alert.error,
                            marginBottom: theme.spacing.lg,
                        }}>
                            <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
                            <div>
                                <strong>Error</strong>
                                <p style={{ margin: '4px 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.status.error }}>
                                    {error}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Password Form (only if password not already set) */}
                    {!userInfo?.hasPasswordSet && (
                        <form onSubmit={handleSubmit} style={{ marginBottom: theme.spacing.xl }}>
                            <div style={{
                                backgroundColor: theme.colors.surface,
                                borderRadius: theme.borderRadius.md,
                                padding: theme.spacing.lg,
                                border: `1px solid ${theme.colors.border.default}`,
                            }}>
                                <h2 style={{
                                    ...themedStyles.text.heading,
                                    fontSize: theme.typography.fontSize.lg,
                                    marginBottom: theme.spacing.md,
                                }}>
                                    Complete Your Account Setup
                                </h2>

                                <div style={{ marginBottom: theme.spacing.md }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: theme.spacing.xs,
                                        fontSize: theme.typography.fontSize.sm,
                                        fontWeight: theme.typography.fontWeight.medium,
                                        color: theme.colors.text.primary,
                                    }}>
                                        New Password *
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="newPassword"
                                            value={passwordData.newPassword}
                                            onChange={(e) => {
                                                setPasswordData(prev => ({ ...prev, newPassword: e.target.value }));
                                                if (passwordErrors.newPassword) {
                                                    setPasswordErrors(prev => ({ ...prev, newPassword: undefined }));
                                                }
                                                setError(null);
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: theme.spacing.sm,
                                                paddingRight: '40px',
                                                fontSize: theme.typography.fontSize.sm,
                                                border: `1px solid ${passwordErrors.newPassword ? theme.colors.status.error : theme.colors.border.default}`,
                                                borderRadius: theme.borderRadius.sm,
                                                backgroundColor: theme.colors.surface,
                                                color: theme.colors.text.primary,
                                                boxSizing: 'border-box' as const,
                                            }}
                                            placeholder="Enter your password (min. 8 characters)"
                                            disabled={isSubmittingPassword || isSubmittingTerms}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => togglePasswordVisibility('password')}
                                            style={{
                                                position: 'absolute',
                                                right: theme.spacing.xs,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: 'none',
                                                border: 'none',
                                                color: theme.colors.text.secondary,
                                                cursor: 'pointer',
                                                padding: theme.spacing.xs,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                        </button>
                                    </div>
                                    {passwordErrors.newPassword && (
                                        <p style={{
                                            marginTop: theme.spacing.xs,
                                            fontSize: theme.typography.fontSize.xs,
                                            color: theme.colors.status.error,
                                        }}>
                                            {passwordErrors.newPassword}
                                        </p>
                                    )}
                                </div>

                                <div style={{ marginBottom: theme.spacing.md }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: theme.spacing.xs,
                                        fontSize: theme.typography.fontSize.sm,
                                        fontWeight: theme.typography.fontWeight.medium,
                                        color: theme.colors.text.primary,
                                    }}>
                                        Confirm Password *
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            name="confirmPassword"
                                            value={passwordData.confirmPassword}
                                            onChange={(e) => {
                                                setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }));
                                                if (passwordErrors.confirmPassword) {
                                                    setPasswordErrors(prev => ({ ...prev, confirmPassword: undefined }));
                                                }
                                                setError(null);
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: theme.spacing.sm,
                                                paddingRight: '40px',
                                                fontSize: theme.typography.fontSize.sm,
                                                border: `1px solid ${passwordErrors.confirmPassword ? theme.colors.status.error : theme.colors.border.default}`,
                                                borderRadius: theme.borderRadius.sm,
                                                backgroundColor: theme.colors.surface,
                                                color: theme.colors.text.primary,
                                                boxSizing: 'border-box' as const,
                                            }}
                                            placeholder="Confirm your password"
                                            disabled={isSubmittingPassword || isSubmittingTerms}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => togglePasswordVisibility('confirm')}
                                            style={{
                                                position: 'absolute',
                                                right: theme.spacing.xs,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: 'none',
                                                border: 'none',
                                                color: theme.colors.text.secondary,
                                                cursor: 'pointer',
                                                padding: theme.spacing.xs,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                                        </button>
                                    </div>
                                    {passwordErrors.confirmPassword && (
                                        <p style={{
                                            marginTop: theme.spacing.xs,
                                            fontSize: theme.typography.fontSize.xs,
                                            color: theme.colors.status.error,
                                        }}>
                                            {passwordErrors.confirmPassword}
                                        </p>
                                    )}
                                </div>

                                {/* Accept Toggle and Submit Button - Inside password form, above T&C */}
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: theme.spacing.md,
                                    marginTop: theme.spacing.lg,
                                }}>
                                    <CustomToggle
                                        checked={hasAcceptedTerms}
                                        onChange={(checked) => {
                                            setHasAcceptedTerms(checked);
                                            setError(null);
                                        }}
                                        disabled={isSubmittingPassword || isSubmittingTerms}
                                        label="I have read and agree to the Terms and Conditions and Privacy Policy. *"
                                        labelStyle={{
                                            fontSize: theme.typography.fontSize.sm,
                                            color: theme.colors.text.primary,
                                        }}
                                    />

                                    <button
                                        type="submit"
                                        disabled={!isButtonEnabled()}
                                        style={{
                                            ...themedStyles.button.primary,
                                            flexShrink: 0,
                                            opacity: !isButtonEnabled() ? 0.6 : 1,
                                            cursor: !isButtonEnabled() ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        {(isSubmittingPassword || isSubmittingTerms) ? 'Completing Setup...' : 'Complete Account Setup'}
                                    </button>
                                </div>

                                {/* Error message display - shown when validation fails */}
                                {error && (
                                    <div style={{
                                        marginTop: theme.spacing.md,
                                        padding: theme.spacing.sm,
                                        backgroundColor: '#fee',
                                        border: `1px solid ${theme.colors.status.error}`,
                                        borderRadius: theme.borderRadius.sm,
                                        color: theme.colors.status.error,
                                        fontSize: theme.typography.fontSize.sm,
                                    }}>
                                        {error}
                                    </div>
                                )}

                                {/* Terms and Conditions Header */}
                                <h3 style={{
                                    ...themedStyles.text.heading,
                                    fontSize: theme.typography.fontSize.md,
                                    marginTop: theme.spacing.lg,
                                    marginBottom: theme.spacing.sm,
                                }}>
                                    Calculist Terms and Conditions
                                </h3>

                                {/* Terms and Conditions Content - Inside password form */}
                                <div style={{
                                    backgroundColor: theme.colors.background,
                                    borderRadius: theme.borderRadius.md,
                                    padding: theme.spacing.lg,
                                    marginTop: theme.spacing.sm,
                                    border: `1px solid ${theme.colors.border.default}`,
                                    maxHeight: '40vh',
                                    overflow: 'auto',
                                }}>
                                <div
                                    style={{
                                        fontSize: theme.typography.fontSize.sm,
                                        lineHeight: 1.8,
                                        color: theme.colors.text.primary,
                                    }}
                                >
                                    <TermsAndConditionsContent />
                                </div>
                            </div>
                        </div>
                        </form>
                    )}

                    {/* Terms and Conditions Content - Only shown if password already set */}
                    {userInfo?.hasPasswordSet && (
                        <>
                            <div style={{
                                backgroundColor: theme.colors.surface,
                                borderRadius: theme.borderRadius.md,
                                padding: theme.spacing.xl,
                                marginBottom: theme.spacing.lg,
                                border: `1px solid ${theme.colors.border.default}`,
                                maxHeight: '40vh',
                                overflow: 'auto',
                            }}>
                                <div
                                    style={{
                                        fontSize: theme.typography.fontSize.sm,
                                        lineHeight: 1.8,
                                        color: theme.colors.text.primary,
                                    }}
                                >
                                    <TermsAndConditionsContent />
                                </div>
                            </div>

                            {/* Accept Toggle and Submit Button - Only shown if password already set */}
                            <div style={{
                                backgroundColor: theme.colors.surface,
                                borderRadius: theme.borderRadius.md,
                                padding: theme.spacing.lg,
                                border: `1px solid ${theme.colors.border.default}`,
                            }}>
                                <CustomToggle
                                    checked={hasAcceptedTerms}
                                    onChange={(checked) => {
                                        setHasAcceptedTerms(checked);
                                        setError(null);
                                    }}
                                    disabled={isSubmittingPassword || isSubmittingTerms}
                                    label="I have read and agree to the Terms and Conditions and Privacy Policy. *"
                                    labelStyle={{
                                        fontSize: theme.typography.fontSize.sm,
                                        color: theme.colors.text.primary,
                                        marginBottom: theme.spacing.md,
                                    }}
                                />

                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={!isButtonEnabled()}
                                    style={{
                                        ...themedStyles.button.primary,
                                        width: '100%',
                                        opacity: !isButtonEnabled() ? 0.6 : 1,
                                        cursor: !isButtonEnabled() ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {(isSubmittingPassword || isSubmittingTerms) ? 'Completing Setup...' : 'Complete Account Setup'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // This should never be reached, but TypeScript needs it
    return null;
};
